/**
 * Tickets API — клиент тикет-системы Рублокса.
 *
 * Бэкенд: storys-микросервис, эндпоинты /api-storys/kubikon3d/tickets/*.
 * См. план: 1 - Неделя 4\3 - Баг-репорты и связь с МИНом\PLAN.md
 *
 * Использует общий request() из api/client — он сам подставляет JWT
 * (сырой, без префикса Bearer), парсит ответы, переводит коды ошибок.
 */
import { request, API_BASE, getToken } from "./client";

const PREFIX = "/api-storys/kubikon3d/tickets";

/* ── публичные ── */

/**
 * Создать тикет.
 *
 * Поля payload:
 *   subject, category, priority, text, attachments?: number[]
 *   guest_email?, captcha_token?   — только для гостей
 *
 * auth=true когда залогинен; гостевой запрос идёт без токена.
 */
export function createTicket(payload, { authed = true } = {}) {
  return request(`${PREFIX}`, {
    method: "POST",
    body: payload,
    auth: authed,
  });
}

/** Список моих тикетов (только для залогиненных). */
export function getMyTickets() {
  return request(`${PREFIX}/my`, { auth: true });
}

/** Детали тикета + сообщения. */
export function getTicket(id) {
  return request(`${PREFIX}/${id}`, { auth: true });
}

/** Добавить сообщение в тикет от юзера. */
export function addMessage(id, payload) {
  return request(`${PREFIX}/${id}/messages`, {
    method: "POST",
    body: payload,
    auth: true,
  });
}

/** Юзер закрыл свой тикет («решено»). */
export function closeByUser(id) {
  return request(`${PREFIX}/${id}/close-by-user`, {
    method: "POST",
    auth: true,
  });
}

/** Подсказать категорию по subject. */
export function autoCategorize(subject) {
  return request(`${PREFIX}/auto-categorize`, {
    method: "POST",
    body: { subject },
  });
}

/** Поиск похожих FAQ при наборе subject. */
export function searchFaq(q, signal) {
  return request(`${PREFIX}/search-faq?q=${encodeURIComponent(q)}`, { signal });
}

/** Список FAQ-статей сгруппированный по категориям. */
export function listFaq() {
  return request(`${PREFIX}/faq`);
}

/** Одна FAQ-статья. */
export function getFaq(id) {
  return request(`${PREFIX}/faq/${id}`);
}

/** Оценить статью FAQ. */
export function faqHelpful(id, value) {
  return request(`${PREFIX}/faq/${id}/helpful`, {
    method: "POST",
    body: { value },
  });
}

/**
 * Загрузка вложения. Не через request() — нужен multipart.
 * Возвращает {file_id}.
 *
 * captchaToken — обязателен для гостей.
 */
export async function uploadAttachment(file, { captchaToken } = {}) {
  const fd = new FormData();
  fd.append("file", file);
  if (captchaToken) fd.append("captcha_token", captchaToken);
  const headers = {};
  const token = getToken();
  if (token) headers["Authorization"] = token; // сырой JWT, без Bearer

  const res = await fetch(`${API_BASE}${PREFIX}/upload-attachment`, {
    method: "POST",
    headers,
    body: fd,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    const err = new Error((data && data.error) || "upload_failed");
    err.code = data && data.error;
    err.status = res.status;
    throw err;
  }
  return data;
}

/**
 * URL для отображения inline (img src или video src).
 *
 * Подмешивает ?token=<JWT>, потому что <img>/<video> не отправляют
 * заголовок Authorization (только fetch это умеет). Бэк-хелпер
 * resolve_user_id_from_token читает токен и из query, и из header.
 */
export function attachmentUrl(id) {
  const token = getToken();
  const base = `${API_BASE}${PREFIX}/attachments/${id}`;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}
